/**
 * Chart Data Transformers
 * Transform projection API data into chart-ready formats
 */

import {
  ProjectionResponse,
  HouseholdYearProjection,
  CashFlowDataPoint,
  PortfolioDataPoint,
  TaxDataPoint,
  NetWorthDataPoint,
  NetWorthSeries,
  NetWorthChartData,
  ChartAnnotations,
} from '../types/projection';
import type { MonteCarloYearStats } from '../types/projection';
import { parseLocalDate } from '../types/wizard';
import { CHART_COLORS } from '../constants/chartColors';
import { toPeopleForLabel, getInitials, type PersonAgeInput } from '../utils/yearAgeLabel';

// Re-exported: chart reference-line labels are space-constrained too.
export { getInitials };

/**
 * Everyone in the plan for a given year, as the label helpers want them.
 *
 * Reads `persons` directly rather than the old baseAges + year-offset trick:
 * that existed to keep ages incrementing past life expectancy, which meant a
 * tooltip in 2062 still aged a spouse the plan had buried in 2054. The label
 * helpers drop the deceased instead, so the real per-year `isAlive` flag is
 * what we need to carry.
 */
function toPeople(projection: HouseholdYearProjection): PersonAgeInput[] {
  return toPeopleForLabel(projection.persons);
}

/**
 * Transform projection data to cash flow chart format
 */
/**
 * A projection figure must be a real number before it can be charted.
 *
 * TypeScript declaring a field required is a claim about the object's shape, not
 * about what crosses the network: `roundToMoney(NaN)` is `NaN`, and
 * `JSON.stringify(NaN)` is `"null"`, so a required `number` can arrive as
 * `null`. Substituting a plausible stand-in at that point is the worst outcome
 * available — a user reading a chart cannot tell an imputed figure from a real
 * one, and the substitutes that used to sit here (household income for
 * spending, 0 for an estate) both read as reassuring news.
 *
 * Throwing is caught by RouteErrorBoundary, so the user sees that something is
 * wrong rather than a confident wrong number.
 */
function requireFinite(value: number | undefined | null, field: string, year: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(
      `Projection year ${year} has no usable ${field} (received ${String(value)}). ` +
        'Refusing to chart an imputed figure — regenerate the projection.'
    );
  }
  return value;
}

export function transformToCashFlowData(
  data: ProjectionResponse
  // `annualSpending` used to feed a fallback that substituted a stand-in figure
  // for spending. That fallback is gone, so the parameter has no purpose.
): CashFlowDataPoint[] {
  return data.yearlyProjections.map(year => {
    // Aggregate income sources across all persons
    const statePension = year.persons.reduce((sum, p) => sum + p.income.statePension, 0);
    const additionalPensions = year.persons.reduce((sum, p) => sum + p.income.additionalPensions, 0);
    const annuityIncome = year.persons.reduce((sum, p) => sum + (p.income.annuityIncome ?? 0), 0);
    const supplementaryIncome = year.persons.reduce((sum, p) => sum + (p.income.supplementaryIncome ?? 0), 0);
    const sipp = year.persons.reduce((sum, p) => sum + p.income.sippGross, 0);
    const isa = year.persons.reduce((sum, p) => sum + p.income.isaWithdrawal, 0);
    const brokerage = year.persons.reduce((sum, p) => sum + (p.income.brokerageWithdrawal ?? 0), 0);
    const cash = year.persons.reduce((sum, p) => sum + p.income.cashWithdrawal, 0);
    const property = year.persons.reduce((sum, p) => sum + p.income.propertyEquityReleased, 0);
    const rentalIncome = year.rentalIncome ?? 0;

    const careCostsTotal = (year.spendingBreakdown?.careCosts ?? [])
      .reduce((sum, c) => sum + c.amount, 0);

    const point: CashFlowDataPoint = {
      year: year.year,
      calendarYear: year.calendarYear,
      people: toPeople(year),
      statePension,
      additionalPensions,
      annuityIncome,
      supplementaryIncome,
      sipp,
      isa,
      brokerage,
      cash,
      property,
      rentalIncome,
      totalIncome: year.householdIncome,
      spending: requireFinite(year.spendingBreakdown?.total, 'spendingBreakdown.total', year.year),
      careCosts: careCostsTotal,
      mortgageCost: requireFinite(year.spendingBreakdown?.mortgageCost, 'spendingBreakdown.mortgageCost', year.year),
      spendingPhases: year.spendingBreakdown?.phases,
      spendingOneTime: year.spendingBreakdown?.oneTime,
      spendingRecurring: year.spendingBreakdown?.recurring,
    };

    // Populate guardrail corridor when available
    if (year.guardrailInfo) {
      point.spendingFloor = year.guardrailInfo.floorSpending;
      point.spendingCeiling = year.guardrailInfo.ceilingSpending;
      point.guardrailEvent = year.guardrailInfo.event;
      point.targetSpending = year.guardrailInfo.targetSpending;
      // Use adjusted spending from guardrail when available
      if (year.guardrailInfo.adjustedSpending != null) {
        point.spending = year.guardrailInfo.adjustedSpending;
      }
    }

    return point;
  });
}

/**
 * Transform projection data to portfolio balance chart format
 */
export function transformToPortfolioData(data: ProjectionResponse): PortfolioDataPoint[] {
  return data.yearlyProjections.map(year => ({
    year: year.year,
    calendarYear: year.calendarYear,
    people: toPeople(year),
    sipp: year.householdPortfolio.sipp,
    isa: year.householdPortfolio.isa,
    brokerage: requireFinite(year.householdPortfolio.brokerage, 'householdPortfolio.brokerage', year.year),
    cash: year.householdPortfolio.cash,
    total: year.householdPortfolio.totalPortfolio,
  }));
}

/**
 * Transform projection data to tax chart format
 */
export function transformToTaxData(data: ProjectionResponse): TaxDataPoint[] {
  let cumulativeTax = 0;

  return data.yearlyProjections.map(year => {
    // Aggregate tax across all persons
    const basicRate = year.persons.reduce((sum, p) => sum + p.tax.basicRateTax, 0);
    const higherRate = year.persons.reduce((sum, p) => sum + p.tax.higherRateTax, 0);
    const additionalRate = year.persons.reduce((sum, p) => sum + p.tax.additionalRateTax, 0);
    const totalTax = year.householdTax;

    cumulativeTax += totalTax;

    return {
      year: year.year,
      calendarYear: year.calendarYear,
      people: toPeople(year),
      basicRate,
      higherRate,
      additionalRate,
      totalTax,
      cumulativeTax,
    };
  });
}

/**
 * Transform projection data to net worth chart format with per-account breakdown
 */
export function transformToNetWorthData(
  data: ProjectionResponse
): NetWorthChartData {
  const firstYear = data.yearlyProjections[0];
  const isJoint = firstYear !== undefined && firstYear.persons.length > 1;

  const chartData: NetWorthDataPoint[] = data.yearlyProjections.map(year => {
    const propertyMarketValue = year.propertyMarketValue ?? 0;
    const outstandingMortgage = year.outstandingMortgage ?? 0;
    const netPropertyEquity = Math.max(0, propertyMarketValue - outstandingMortgage);
    const point: NetWorthDataPoint = {
      year: year.year,
      calendarYear: year.calendarYear,
      people: toPeople(year),
      netWorth: year.householdPortfolio.totalPortfolio + netPropertyEquity,
      propertyMarketValue,
      netPropertyEquity: netPropertyEquity > 0 ? netPropertyEquity : undefined,
      outstandingMortgage: outstandingMortgage > 0 ? outstandingMortgage : undefined,
    };

    year.persons.forEach((p, idx) => {
      const prefix = isJoint ? `p${idx}_` : '';
      point[`${prefix}sipp`] = p.portfolioEnd.sipp;
      point[`${prefix}isa`] = p.portfolioEnd.isa;
      point[`${prefix}brokerage`] = requireFinite(p.portfolioEnd.brokerage, 'portfolioEnd.brokerage', year.year);
      point[`${prefix}cash`] = p.portfolioEnd.cash;
    });

    return point;
  });

  const PROPERTY_SERIES: NetWorthSeries = { dataKey: 'netPropertyEquity', label: 'Property (net of mortgage)', color: CHART_COLORS.property };

  const SINGLE_SERIES: NetWorthSeries[] = [
    { dataKey: 'sipp', label: 'SIPP', color: CHART_COLORS.sipp },
    { dataKey: 'isa', label: 'ISA', color: CHART_COLORS.isa },
    { dataKey: 'brokerage', label: 'GIA', color: CHART_COLORS.brokerage },
    { dataKey: 'cash', label: 'Cash', color: CHART_COLORS.cash },
  ];

  // Include property series definition — toggling is handled by the chart component
  const hasPropertyData = chartData.some(d => ((d.netPropertyEquity as number) ?? 0) > 0);

  if (!isJoint) {
    const series = hasPropertyData ? [PROPERTY_SERIES, ...SINGLE_SERIES] : SINGLE_SERIES;
    return { data: chartData, series };
  }

  const p0Name = firstYear.persons[0]?.personName || 'Person 1';
  const p1Name = firstYear.persons[1]?.personName || 'Person 2';
  const series: NetWorthSeries[] = [
    ...(hasPropertyData ? [PROPERTY_SERIES] : []),
    { dataKey: 'p0_sipp', label: `${p0Name} SIPP`, color: CHART_COLORS.sipp },
    { dataKey: 'p0_isa', label: `${p0Name} ISA`, color: CHART_COLORS.isa },
    { dataKey: 'p0_brokerage', label: `${p0Name} GIA`, color: CHART_COLORS.brokerage },
    { dataKey: 'p0_cash', label: `${p0Name} Cash`, color: CHART_COLORS.cash },
    { dataKey: 'p1_sipp', label: `${p1Name} SIPP`, color: CHART_COLORS.person2Sipp },
    { dataKey: 'p1_isa', label: `${p1Name} ISA`, color: CHART_COLORS.person2Isa },
    { dataKey: 'p1_brokerage', label: `${p1Name} GIA`, color: CHART_COLORS.person2Brokerage },
    { dataKey: 'p1_cash', label: `${p1Name} Cash`, color: CHART_COLORS.person2Cash },
  ];

  return { data: chartData, series };
}

/**
 * Merged data point with optional shock scenario total
 */
export interface ShockOverlayDataPoint extends PortfolioDataPoint {
  shockTotal?: number;
}

/**
 * Merge base portfolio data with shock scenario data for chart overlay.
 * Adds a `shockTotal` field to each data point.
 */
export function mergeShockOverlay(
  baseData: PortfolioDataPoint[],
  shockData: PortfolioDataPoint[],
): ShockOverlayDataPoint[] {
  const shockByYear = new Map<number, PortfolioDataPoint>();
  shockData.forEach(point => shockByYear.set(point.calendarYear, point));

  return baseData.map(point => ({
    ...point,
    shockTotal: shockByYear.get(point.calendarYear)?.total,
  }));
}

/**
 * Get depletion year index for chart marking
 */
export function getDepletionYearIndex(data: ProjectionResponse): number | null {
  const depletionYear = data.summary.depletionYear;
  if (depletionYear === null) return null;

  const index = data.yearlyProjections.findIndex(y => y.year === depletionYear);
  return index >= 0 ? index : null;
}

/**
 * Format currency for tooltips
 */
export function formatCurrency(value: number): string {
  if (!Number.isFinite(value)) return '£0';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format large currency values (abbreviate thousands/millions)
 */
export function formatCurrencyCompact(value: number): string {
  if (value >= 1_000_000) {
    return `£${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `£${(value / 1_000).toFixed(0)}k`;
  }
  return `£${value.toFixed(0)}`;
}

// ============================================
// Chart Annotations
// ============================================

interface FormPerson {
  fullName: string;
  dateOfBirth: string;
  retirementDate: string;
  statePensionAge: number;
}

interface FormSpendingPhase {
  label: string;
  startYear: number;
  endYear?: number;
}

interface FormCareScenario {
  personIndex: number;
  careType: string;
  startYear: number;
  durationYears: number;
}

interface FormDeceasedAge {
  personIndex: number;
  deceasedAge: number | null;
}

const CARE_TYPE_LABELS: Record<string, string> = {
  HOME: 'Home Care',
  RESIDENTIAL: 'Residential',
  NURSING: 'Nursing',
};

function computeAgeAtDate(dob: Date, target: Date): number {
  let age = target.getFullYear() - dob.getFullYear();
  const monthDiff = target.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && target.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

/**
 * Build phase annotations from spending phases.
 */
function buildPhaseAnnotations(
  spendingPhases: FormSpendingPhase[] | undefined,
  baseCalendarYear: number,
  lastCalendarYear?: number,
): ChartAnnotations['phases'] {
  if (!spendingPhases) return [];

  const phases: ChartAnnotations['phases'] = [];
  for (const phase of spendingPhases) {
    if (!phase.label || phase.startYear == null) continue;
    phases.push({
      label: phase.label,
      startCalendarYear: baseCalendarYear + phase.startYear - 1,
      // Open-ended final phase: extend annotation to end of projection
      endCalendarYear: phase.endYear !== null && phase.endYear !== undefined
        ? baseCalendarYear + phase.endYear
        : (lastCalendarYear ?? baseCalendarYear + 50),
    });
  }
  return phases;
}

/**
 * Build state pension start annotations.
 * Engine computes initial age at Jan 1 of startYear, then increments before checking.
 * Year N: age = ageAtJan1 + N, calendarYear = baseCalendarYear + N - 1
 * Event fires when ageAtJan1 + N >= targetAge → N = targetAge - ageAtJan1
 * calendarYear = baseCalendarYear + (targetAge - ageAtJan1) - 1
 */
function buildStatePensionAnnotations(
  persons: FormPerson[],
  baseCalendarYear: number,
): ChartAnnotations['statePensionStarts'] {
  const startOfYear = new Date(baseCalendarYear, 0, 1);
  const statePensionStarts: ChartAnnotations['statePensionStarts'] = [];

  for (const person of persons) {
    const dob = parseLocalDate(person.dateOfBirth);
    if (!dob) continue;

    const ageAtStart = computeAgeAtDate(dob, startOfYear);

    if (person.statePensionAge > ageAtStart) {
      const spCalendarYear = baseCalendarYear + (person.statePensionAge - ageAtStart) - 1;
      statePensionStarts.push({
        personName: person.fullName,
        calendarYear: spCalendarYear,
      });
    }
  }

  return statePensionStarts;
}

/**
 * Build care period annotations.
 */
function buildCarePeriodAnnotations(
  careScenarios: FormCareScenario[] | undefined,
  persons: FormPerson[],
  baseCalendarYear: number,
): ChartAnnotations['carePeriods'] {
  if (!careScenarios) return [];

  const carePeriods: ChartAnnotations['carePeriods'] = [];
  for (const scenario of careScenarios) {
    const person = persons[scenario.personIndex];
    if (!person) continue;
    const startCal = baseCalendarYear + scenario.startYear - 1;
    const endCal = startCal + scenario.durationYears - 1;
    carePeriods.push({
      personName: person.fullName,
      careType: CARE_TYPE_LABELS[scenario.careType] ?? scenario.careType,
      startCalendarYear: startCal,
      endCalendarYear: endCal,
    });
  }

  return carePeriods;
}

/**
 * Build life expectancy annotations using the same age calculation as state pension.
 */
function buildLifeExpectancyAnnotations(
  personDeceasedAges: FormDeceasedAge[] | undefined,
  persons: FormPerson[],
  baseCalendarYear: number,
): ChartAnnotations['lifeExpectancies'] {
  if (!personDeceasedAges) return [];

  const startOfYear = new Date(baseCalendarYear, 0, 1);
  const lifeExpectancies: ChartAnnotations['lifeExpectancies'] = [];

  for (const entry of personDeceasedAges) {
    if (entry.deceasedAge == null) continue;
    const person = persons[entry.personIndex];
    if (!person) continue;
    const dob = parseLocalDate(person.dateOfBirth);
    if (!dob) continue;
    const ageAtStart = computeAgeAtDate(dob, startOfYear);
    // deceasedAge is inclusive — person lives through this age, so annotation sits at this year
    const leCalendarYear = baseCalendarYear + (entry.deceasedAge - ageAtStart);
    lifeExpectancies.push({
      personName: person.fullName,
      calendarYear: leCalendarYear,
    });
  }

  return lifeExpectancies;
}

/**
 * Build chart annotations from wizard form data stored in sessionStorage.
 * Annotations falling outside [baseCalendarYear, lastCalendarYear] are filtered out.
 * Returns null if form data is missing or invalid.
 */
export function buildChartAnnotations(
  formData: Record<string, unknown>,
  baseCalendarYear: number,
  lastCalendarYear?: number,
): ChartAnnotations | null {
  const rawPersons = formData.persons;
  if (!Array.isArray(rawPersons) || rawPersons.length === 0) return null;
  const persons = rawPersons as FormPerson[];

  const annotations: ChartAnnotations = {
    phases: buildPhaseAnnotations(
      formData.spendingPhases as FormSpendingPhase[] | undefined,
      baseCalendarYear,
      lastCalendarYear,
    ),
    statePensionStarts: buildStatePensionAnnotations(persons, baseCalendarYear),
    carePeriods: buildCarePeriodAnnotations(
      formData.careScenarios as FormCareScenario[] | undefined,
      persons,
      baseCalendarYear,
    ),
    lifeExpectancies: buildLifeExpectancyAnnotations(
      formData.personDeceasedAges as FormDeceasedAge[] | undefined,
      persons,
      baseCalendarYear,
    ),
  };

  // Add retirement boundary when contributions are active
  if (formData.contributions && typeof formData.contributions === 'object') {
    const contribs = formData.contributions as Record<string, { makingContributions?: boolean }>;
    const hasActiveContribs = Object.values(contribs).some(c => c?.makingContributions);
    if (hasActiveContribs) {
      annotations.retirementStarts = persons.map(p => ({
        personName: p.fullName,
        calendarYear: new Date(p.retirementDate).getFullYear(),
      })).filter(r => r.calendarYear > baseCalendarYear);
    }
  }

  // Filter and clip annotations to the chart data range
  if (lastCalendarYear != null) {
    annotations.statePensionStarts = annotations.statePensionStarts.filter(
      sp => sp.calendarYear >= baseCalendarYear && sp.calendarYear <= lastCalendarYear,
    );
    annotations.lifeExpectancies = annotations.lifeExpectancies.filter(
      le => le.calendarYear >= baseCalendarYear && le.calendarYear <= lastCalendarYear,
    );
    annotations.carePeriods = annotations.carePeriods
      .filter(cp => cp.startCalendarYear <= lastCalendarYear && cp.endCalendarYear >= baseCalendarYear)
      .map(cp => ({
        ...cp,
        startCalendarYear: Math.max(cp.startCalendarYear, baseCalendarYear),
        endCalendarYear: Math.min(cp.endCalendarYear, lastCalendarYear),
      }));
    annotations.phases = annotations.phases
      .filter(p => p.startCalendarYear <= lastCalendarYear && p.endCalendarYear >= baseCalendarYear)
      .map(p => ({
        ...p,
        startCalendarYear: Math.max(p.startCalendarYear, baseCalendarYear),
        endCalendarYear: Math.min(p.endCalendarYear, lastCalendarYear),
      }));
  }

  return annotations;
}

// ============================================
// Monte Carlo Fan Chart
// ============================================

export interface FanChartDataPoint {
  year: number;
  calendarYear: number;
  people: PersonAgeInput[];
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  deterministic: number;
  // Computed band deltas for stacked area rendering:
  base: number;       // = p10 (bottom of stack)
  band10_25: number;  // = p25 - p10
  band25_75: number;  // = p75 - p25
  band75_90: number;  // = p90 - p75
}

/**
 * Data shape accepted by the fan chart transformer.
 * Both MonteCarloResponse and HistoricalReplayResponse satisfy this.
 */
interface FanChartInput {
  yearlyStats: MonteCarloYearStats[];
  deterministicBaseline: { yearlyTotals: number[] };
}

/**
 * Transform Monte Carlo or Historical Replay response into fan chart data points.
 * Uses stacked area bands: base (p10) → p25 → p75 → p90.
 */
export function transformToFanChartData(
  mcResponse: FanChartInput,
): FanChartDataPoint[] {
  return mcResponse.yearlyStats.map((stats, i) => ({
    year: stats.year,
    calendarYear: stats.calendarYear,
    // Monte Carlo yearlyStats carries only a joined "81/79" string — no names,
    // no per-person alive flag — so names are unavailable here by construction.
    people: stats.ages.split('/').map((age) => ({ name: '', age: Number(age.trim()) })),
    p10: stats.p10,
    p25: stats.p25,
    p50: stats.p50,
    p75: stats.p75,
    p90: stats.p90,
    deterministic: mcResponse.deterministicBaseline.yearlyTotals[i] ?? 0,
    base: stats.p10,
    band10_25: Math.max(0, stats.p25 - stats.p10),
    band25_75: Math.max(0, stats.p75 - stats.p25),
    band75_90: Math.max(0, stats.p90 - stats.p75),
  }));
}

// ============================================
// Estate Composition (IHT) Transformer
// ============================================

export interface EstateCompositionDataPoint {
  year: number;
  calendarYear: number;
  people: PersonAgeInput[];
  property: number;
  sipp: number;
  isa: number;
  brokerage: number;
  cash: number;
  estateTotal: number;
  nrbThreshold: number;
  rnrbThreshold: number;
  ihtLiability: number;
}

export function transformToEstateCompositionData(
  response: ProjectionResponse
): EstateCompositionDataPoint[] {
  if (!response.yearlyProjections.length) return [];

  return response.yearlyProjections
    .filter(yr => yr.ihtExposure != null)
    .map((yr) => {
      const iht = yr.ihtExposure!;
      const comp = iht.estateComposition;

      return {
        year: yr.year,
        calendarYear: yr.calendarYear,
        people: toPeople(yr),
        // `?? 0` here rendered a fully-formed, all-zero estate — a £0 liability
        // against a £0 nil-rate band. That does not look like an error, it looks
        // like good news, and a user with real IHT exposure would act on it.
        // ihtExposure is complete-or-absent (the filter above drops absent
        // years), so a missing field means the payload is broken.
        property: requireFinite(comp?.propertyValue, 'estateComposition.propertyValue', yr.year),
        sipp: requireFinite(comp?.pensionValue, 'estateComposition.pensionValue', yr.year),
        isa: requireFinite(comp?.isaValue, 'estateComposition.isaValue', yr.year),
        brokerage: requireFinite(comp?.brokerageValue, 'estateComposition.brokerageValue', yr.year),
        cash: requireFinite(comp?.cashValue, 'estateComposition.cashValue', yr.year),
        estateTotal: requireFinite(iht.estateValue, 'ihtExposure.estateValue', yr.year),
        nrbThreshold:
          requireFinite(iht.nrb, 'ihtExposure.nrb', yr.year) +
          requireFinite(iht.transferableNrb, 'ihtExposure.transferableNrb', yr.year),
        rnrbThreshold:
          requireFinite(iht.nrb, 'ihtExposure.nrb', yr.year) +
          requireFinite(iht.transferableNrb, 'ihtExposure.transferableNrb', yr.year) +
          requireFinite(iht.rnrb, 'ihtExposure.rnrb', yr.year) +
          requireFinite(iht.transferableRnrb, 'ihtExposure.transferableRnrb', yr.year),
        ihtLiability: requireFinite(iht.ihtLiability, 'ihtExposure.ihtLiability', yr.year),
      };
    });
}
